import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../store";

export const selectBrief = (state: RootState) => state.brief;

export const selectBriefs = createSelector(selectBrief, (state) => {
  return state.entities;
});


export const selectSelectedBriefOutlines = createSelector(
  selectBrief,
  (state) => state.selectedOutlines
);

export const selectCurrentBrief = createSelector(selectBrief, (state) => {
  if (state.currentBriefId === "") return null;
  return state.entities.find((brief) => brief?.id === state.currentBriefId);
});

export const selectBriefById = (briefId: string) =>
  createSelector(selectBrief, (state) => {
    return state.entities.find((brief) => brief?.id === briefId);
  });


export const selectCompetitors = createSelector(selectCurrentBrief, (state) =>
  state?.competitors.filter((competitor) => competitor.type === "organic")
);

export const selectCompetitorsWithKeywords = createSelector(
  selectCompetitors,
  (state) =>
    state.map((competitor) => {
      const uniqueKeywords = competitor.keywords?.items?.filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            ({ keyword_data: { keyword } }) =>
              keyword === value.keyword_data.keyword
          )
      );
      return {
        ...competitor,
        keywords: {
          ...competitor.keywords,
          items:
            uniqueKeywords &&
            [...uniqueKeywords].sort(
              (a, b) =>
                b.keyword_data.keyword_info.search_volume -
                a.keyword_data.keyword_info.search_volume
            ),
        },
      };
    })
);

export const selectCompetitorTitles = createSelector(
  selectCompetitors,
  (competitors) => competitors.map((competitor) => competitor.title)
);

export const selectSelectedOutlines = createSelector(
  selectCurrentBrief,
  (state) => state?.selectedOutlines
);
//insights selectors
export const selectInsights = createSelector(
  selectCurrentBrief,
  (state) => state?.insights
);

export const selectSortedRelatedQuestionsGoogle = createSelector(
  selectCurrentBrief,
  (state) =>
    state.competitors.find(
      (competitor) => competitor.type === "people_also_ask"
    )?.items ?? []
);

export const selectSortedRelatedQuestionsAutocomplete = createSelector(
  selectCurrentBrief,
  (state) =>
    [...state.relatedQuestionsAutocomplete].sort((a, b) => {
      if (a.vol < b.vol) return 1;
      if (a.vol > b.vol) return -1;
      return 0;
    })
);

export const selectSortedRelatedQuestionsQuora = createSelector(
  selectCurrentBrief,
  (state) =>
    state.relatedQuestionsQuora
      ?.map((question) => ({
        ...question,
        title: question.title.replace("- Quora", ""),
      }))
      .filter((question) => question.title.includes("?")) ?? []
);

export const selectSortedRelatedQuestionsReddit = createSelector(
  selectCurrentBrief,
  (state) =>
    state.relatedQuestionsReddit?.map((question) => ({
      ...question,
      title: question.title.replace("- Reddit", ""),
    })) ?? []
);

export const selectLoaders = createSelector(
  selectBrief,
  (state) => state.loading
);

export const selectMaxNumberOfHeadings = createSelector(
  selectCurrentBrief,
  (state) => {
    const heading = state?.competitors.reduce((acc, competitor) => {
      if (competitor.headings?.length && competitor.headings.length > acc) {
        acc = competitor.headings.length;
      }
      return acc;
    }, 0);
    return heading;
  }
);

export const selectMaxNumberOfImages = createSelector(
  selectCurrentBrief,
  (state) => {
    const heading = state?.competitors.reduce((acc, competitor) => {
      if (competitor.images?.length && competitor.images.length > acc) {
        acc = competitor.images.length;
      }
      return acc;
    }, 3);
    return heading;
  }
);

export const selectCompetitorsKeywords = createSelector(
  selectCompetitors,
  (competitors) =>
    competitors.reduce((acc, competitor) => {
      if (!competitor.keywords?.items) {
        return acc;
      }
      const competitorKeyword = competitor.keywords.items.map(
        ({ keyword_data }) => ({
          keyword: keyword_data.keyword,
          searchVolume: keyword_data.keyword_info.search_volume,
        })
      );
      const keywords: string[] = [...new Set([...acc, ...competitorKeyword])];
      return keywords;
    }, [])
);

export const selectCompetitorsDuplicatedKeywords = createSelector(
  selectCompetitors,
  (competitors) => [
    ...new Set(
      competitors
        .reduce((acc, competitor) => {
          if (!competitor.keywords?.items) {
            return acc;
          }
          const competitorKeyword = competitor.keywords.items.map(
            ({ keyword_data }) => keyword_data.keyword
          );
          const keywords: string[] = [...acc, ...competitorKeyword];
          return keywords;
        }, [])
        .filter((e, i, a) => a.indexOf(e) !== i)
    ),
  ]
);

export const selectCompetitorsWithHeadingsAndUrls = createSelector(
  selectCompetitors,
  (competitors) =>
    competitors
      .filter(
        (competitor) => competitor.headings && competitor.headings.length > 0
      )
      .map((competitor) => ({
        headings: competitor.headings,
        url: competitor.url,
      }))
);

export const selectSelectedTopisToCover = createSelector(
  selectCurrentBrief,
  (state) => state.selectedTopics
);

export const selectIsPublic = createSelector(
  selectCurrentBrief,
  (state) => state.isPublic
);

export const selectTopics = createSelector(
  selectCurrentBrief,
  (state) => state.topics ?? []
);

export const selectIsGdocCreating = createSelector(
  selectBrief,
  (brief) => brief.isGdocCreating
);
